/**
 * Scenario Cost Comparison — same policy across locations.
 * Uses full emissions reduction (all sectors) for total reduction and portfolio cost per ton.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
  ScatterController,
  PointElement,
} from 'chart.js';
import { Bar, Scatter } from 'react-chartjs-2';
import { useLocation } from '../../contexts/LocationContext';
import { computeScenarioCostResults, type ScenarioCostResults, type ScenarioCostOptions } from '../../services/scenarioCostModel';
import {
  type CostAssumptions,
  DEFAULT_COST_ASSUMPTIONS,
  type LocationPriceOverrides,
} from '../../services/costAssumptions';
import type { BuildingsCostAssumptions } from '../../services/buildingsCostModel';
import { ICE_MPG, EV_KWH_PER_MILE } from '../../services/transportCostModel';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { fetchLatestElectricityPricesForLocation } from '../../services/electricityPricesByGeographyService';
import { KeyAssumptionsTable } from './KeyAssumptionsTable';
import { getFinancialColor, getFinancialBarColor, FINANCIAL_COLORS, FINANCIAL_COLOR_NOTE } from './portfolioCostUtils';
import type { FinancialColorType } from './portfolioCostUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend, ScatterController, PointElement);

const MAX_LOCATIONS = 5;
const MIN_LOCATIONS = 1;
const ASSUMPTIONS_DEBOUNCE_MS = 600;

/** Format currency with M/B for millions/billions; use |v| for threshold so negatives show as $-X.XXM. */
const currencyShort = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e9) return (v < 0 ? '-$' : '$') + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return (v < 0 ? '-$' : '$') + (abs / 1e6).toFixed(2) + 'M';
  return '$' + Math.round(v).toLocaleString();
};

/** Inline style for the four semantically colored columns only. */
const financialCellStyle = (value: number | null | undefined, type: FinancialColorType): React.CSSProperties => {
  const color = getFinancialColor(value, type);
  return color ? { color } : {};
};

/** Payback: "Never" = muted gray; otherwise neutral. */
const paybackCellStyle = (paybackYear: number | null | undefined): React.CSSProperties =>
  paybackYear == null ? { color: FINANCIAL_COLORS.muted } : {};

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Transport tech not in shared CostAssumptions; defaults from transport model. */
const DEFAULT_ICE_MPG = ICE_MPG;
const DEFAULT_EV_KWH_PER_MILE = EV_KWH_PER_MILE;

/** Heuristic explanation: compare location to first selected (cost per ton). */
function explainCostPerTonVsFirst(
  loc: string,
  firstLoc: string | undefined,
  costPerTon: number | null,
  firstCostPerTon: number | null,
  cumulativeCost: number,
  firstCumulativeCost: number,
  reductionMT: number,
  firstReductionMT: number
): string {
  if (!firstLoc || firstLoc === loc || costPerTon == null || firstCostPerTon == null) return '';
  if (Math.abs(costPerTon - firstCostPerTon) < 1) return `Similar cost per ton to ${firstLoc}.`;
  if (costPerTon > firstCostPerTon) {
    const higherCost = cumulativeCost > firstCumulativeCost;
    const lowerReduction = reductionMT < firstReductionMT;
    if (higherCost && lowerReduction) return `Higher cost per ton vs ${firstLoc}: higher cumulative cost and lower emissions reduction.`;
    if (higherCost) return `Higher cost per ton vs ${firstLoc}: higher cumulative cost.`;
    if (lowerReduction) return `Higher cost per ton vs ${firstLoc}: lower emissions reduction.`;
    return `Higher cost per ton vs ${firstLoc}.`;
  }
  const lowerCost = cumulativeCost < firstCumulativeCost;
  const higherReduction = reductionMT > firstReductionMT;
  if (lowerCost && higherReduction) return `Lower cost per ton vs ${firstLoc}: lower cost and higher emissions reduction.`;
  if (lowerCost) return `Lower cost per ton vs ${firstLoc}: lower cumulative cost.`;
  if (higherReduction) return `Lower cost per ton vs ${firstLoc}: higher emissions reduction.`;
  return `Lower cost per ton vs ${firstLoc}.`;
}

function buildMergedOptions(
  parentOptions: ScenarioCostOptions | undefined,
  assumptions: CostAssumptions,
  locationOverrides: Record<string, LocationPriceOverrides>,
  locations: string[],
  iceMpg: number,
  evKwhPerMile: number,
  costView: 'incremental_vs_bau' | 'bau_costs' | 'total_costs'
): ScenarioCostOptions {
  const priceOverridesByLocation: Record<string, Partial<BuildingsCostAssumptions>> = {};
  const gasolinePriceByLocation: Record<string, number> = {};
  locations.forEach((loc) => {
    const o = locationOverrides[loc];
    priceOverridesByLocation[loc] = {
      ...parentOptions?.priceOverridesByLocation?.[loc],
      electricityPricePerKwh: o?.electricityPricePerKwh ?? assumptions.electricityPricePerKwh,
      naturalGasPricePerTherm: o?.naturalGasPricePerTherm ?? assumptions.naturalGasPricePerTherm,
      weatherizationCostPerHome: assumptions.weatherizationCostPerParticipatingHome,
      resReachIncrementalCostPerUnit: assumptions.residentialReachCodeCostPerNewUnit,
      comReachIncrementalCostPerUnit: assumptions.commercialReachCodeCostPerNewUnit,
      resHeatPumpIncrementalCostPerUnit: assumptions.resHeatPumpIncrementalCostPerUnit,
      comHeatPumpIncrementalCostPerUnit: assumptions.comHeatPumpIncrementalCostPerUnit,
      avgThermsPerResUnit: assumptions.avgThermsPerResUnit,
      avgThermsPerComEquivalentUnit: assumptions.avgThermsPerComEquivalentUnit,
      discountRate: assumptions.discountRate,
      heatPumpRebate: assumptions.heatPumpRebate,
      weatherizationRebate: assumptions.weatherizationRebate,
    };
    if (o?.gasolinePricePerGallon != null && o.gasolinePricePerGallon > 0) {
      gasolinePriceByLocation[loc] = o.gasolinePricePerGallon;
    }
  });
  return {
    ...parentOptions,
    priceOverridesByLocation: { ...parentOptions?.priceOverridesByLocation, ...priceOverridesByLocation },
    gasolinePricePerGallon: assumptions.gasolinePricePerGallon,
    gasolinePriceByLocation: Object.keys(gasolinePriceByLocation).length > 0 ? gasolinePriceByLocation : undefined,
    transportElectricityPricePerKwh: assumptions.electricityPricePerKwh,
    discountRate: assumptions.discountRate,
    iceMpg,
    evKwhPerMile,
    evIncrementalCost: assumptions.evIncrementalCost,
    chargerCostPerEv: assumptions.chargerCostPerEv,
    costView,
  };
}

export interface ScenarioCostAnalysisSectionProps {
  scenario: import('../../services/policyImpactService').PolicyScenario;
  currentLocation: string;
  options?: ScenarioCostOptions;
  /** Only show when results exist (e.g. from main tool). */
  enabled?: boolean;
}

export const ScenarioCostAnalysisSection: React.FC<ScenarioCostAnalysisSectionProps> = ({
  scenario,
  currentLocation,
  options,
  enabled = true,
}) => {
  const { locationData, getCountyForCity } = useLocation();
  const fullLocationList: string[] = locationData?.availableLocations ?? [];

  // Available locations = all cities in current location's county + the county + California (only names that exist in data)
  const availableLocations: string[] = React.useMemo(() => {
    if (fullLocationList.length === 0 || !currentLocation) return fullLocationList;
    const type = supabaseLocationService.getLocationType(currentLocation);
    const countyName = type === 'city' ? getCountyForCity(currentLocation) : type === 'county' ? currentLocation : null;
    const has = new Set(fullLocationList);
    if (!countyName) {
      // State or unknown: show California, all counties, all cities
      const counties = supabaseLocationService.getCounties().map((c) => c.name).filter((n) => has.has(n));
      const cities = supabaseLocationService.getCities().map((c) => c.name).filter((n) => has.has(n));
      return ['California', ...counties.sort(), ...cities.sort()].filter((n) => has.has(n));
    }
    const citiesInCounty = supabaseLocationService.getCitiesInCounty(countyName);
    const base = [countyName, 'California', ...citiesInCounty].filter((n) => has.has(n));
    return Array.from(new Set(base));
  }, [currentLocation, fullLocationList, getCountyForCity]);

  // All cities and all counties for the optional "Add location" dropdown
  const allCitiesAndCounties = React.useMemo(() => {
    const has = new Set(fullLocationList);
    const counties = supabaseLocationService.getCounties().map((c) => c.name).filter((n) => has.has(n)).sort();
    const cities = supabaseLocationService.getCities().map((c) => c.name).filter((n) => has.has(n)).sort();
    const california = has.has('California') ? ['California'] : [];
    return { counties: [...california, ...counties], cities };
  }, [fullLocationList]);

  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [assumptions, setAssumptions] = useState<CostAssumptions>(DEFAULT_COST_ASSUMPTIONS);
  const [locationOverrides, setLocationOverrides] = useState<Record<string, LocationPriceOverrides>>({});
  const [iceMpg, setIceMpg] = useState(DEFAULT_ICE_MPG);
  const [evKwhPerMile, setEvKwhPerMile] = useState(DEFAULT_EV_KWH_PER_MILE);
  const [costView, setCostView] = useState<'incremental_vs_bau' | 'bau_costs' | 'total_costs'>('total_costs');
  const [electricityPriceSource, setElectricityPriceSource] = useState<{ utilityName: string | null; year: number | null; residential: number | null; commercial: number | null; average: number | null } | null>(null);

  // Load electricity price from DB for first selected location; use building price (res+com)/2 for cost assumptions
  useEffect(() => {
    const first = selectedLocations[0] || currentLocation;
    if (!first) return;
    let cancelled = false;
    (async () => {
      try {
        const geofips = await supabaseLocationService.getGeofips(first);
        if (cancelled || geofips == null) return;
        const prices = await fetchLatestElectricityPricesForLocation(geofips);
        if (cancelled) return;
        setElectricityPriceSource({
          utilityName: prices.utilityName,
          year: prices.year,
          residential: prices.residential,
          commercial: prices.commercial,
          average: prices.average,
        });
        const buildingPrice = prices.buildingPricePerKwh ?? prices.average;
        if (buildingPrice != null && buildingPrice > 0) {
          setAssumptions((a) => ({ ...a, electricityPricePerKwh: buildingPrice }));
        }
      } catch {
        if (!cancelled) setElectricityPriceSource(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLocations[0], currentLocation]);

  // Populate location-specific electricity (and gas) from DB for every selected location so cost uses correct prices per location
  useEffect(() => {
    const locs = selectedLocations.filter(Boolean);
    if (locs.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates: Record<string, Partial<LocationPriceOverrides>> = {};
      for (const loc of locs) {
        try {
          const geofips = await supabaseLocationService.getGeofips(loc);
          if (cancelled || geofips == null) continue;
          const prices = await fetchLatestElectricityPricesForLocation(geofips);
          if (cancelled) return;
          const buildingPrice = prices.buildingPricePerKwh ?? prices.average;
          if (buildingPrice != null && buildingPrice > 0) {
            updates[loc] = { ...updates[loc], electricityPricePerKwh: buildingPrice };
          }
        } catch {
          /* skip this location */
        }
      }
      if (cancelled) return;
      if (Object.keys(updates).length > 0) {
        setLocationOverrides((prev) => {
          const next = { ...prev };
          for (const [loc, u] of Object.entries(updates)) {
            next[loc] = { ...prev[loc], ...u };
          }
          return next;
        });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLocations.join(',')]);

  // Default: only current location and California (users can add more up to MAX_LOCATIONS)
  useEffect(() => {
    if (fullLocationList.length === 0 || !currentLocation) return;
    const inList = (name: string) => fullLocationList.some((l) => l === name);
    const parts: string[] = [];
    if (inList(currentLocation)) parts.push(currentLocation);
    if (inList('California')) parts.push('California');
    const valid = Array.from(new Set(parts)).filter(inList);
    setSelectedLocations(valid.slice(0, MAX_LOCATIONS));
  }, [currentLocation, fullLocationList]);

  const [results, setResults] = useState<ScenarioCostResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runComparison = useCallback(async () => {
    const locs = selectedLocations.filter(Boolean);
    if (locs.length < MIN_LOCATIONS) {
      setError(MIN_LOCATIONS === 1 ? 'Select at least one location.' : `Select at least ${MIN_LOCATIONS} locations.`);
      return;
    }
    if (locs.length > MAX_LOCATIONS) {
      setError(`Select at most ${MAX_LOCATIONS} locations.`);
      return;
    }
    setLoading(true);
    setError(null);
    const merged = buildMergedOptions(options, assumptions, locationOverrides, locs, iceMpg, evKwhPerMile, costView);
    try {
      const out = await computeScenarioCostResults({
        scenario,
        locations: locs,
        options: merged,
      });
      setResults(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to compute scenario cost.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [scenario, selectedLocations, options, assumptions, locationOverrides, iceMpg, evKwhPerMile, costView]);

  const locationKey = selectedLocations.slice().sort().join(',');
  useEffect(() => {
    if (!enabled || selectedLocations.length < MIN_LOCATIONS) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const merged = buildMergedOptions(options, assumptions, locationOverrides, selectedLocations, iceMpg, evKwhPerMile, costView);
    computeScenarioCostResults({ scenario, locations: selectedLocations, options: merged })
      .then((out) => { if (!cancelled) setResults(out); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to compute scenario cost.'); setResults(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Refetch when locations or cost view change. Omit scenario/options/assumptions so assumption edits go through debounced effect only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, locationKey, costView]);

  const assumptionsDebounceRan = React.useRef(false);
  useEffect(() => {
    if (!enabled || selectedLocations.length < MIN_LOCATIONS) return;
    if (!assumptionsDebounceRan.current) {
      assumptionsDebounceRan.current = true;
      return;
    }
    const id = setTimeout(() => runComparison(), ASSUMPTIONS_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [assumptions, locationOverrides, iceMpg, evKwhPerMile, costView, enabled, runComparison]);

  const toggleLocation = (loc: string) => {
    setSelectedLocations((prev) => {
      const has = prev.includes(loc);
      if (has) {
        const next = prev.filter((l) => l !== loc);
        return next.length >= MIN_LOCATIONS ? next : prev;
      }
      if (prev.length >= MAX_LOCATIONS) return prev;
      return [...prev, loc];
    });
  };

  const [normalizePerCapita, setNormalizePerCapita] = useState(true);
  const [sensitivityResults, setSensitivityResults] = useState<{
    byLocation: Record<string, { costPerTonBase: number | null; costPerTonMin: number; costPerTonMax: number }>;
  } | null>(null);
  const [sensitivityLoading, setSensitivityLoading] = useState(false);

  const tableRows = results ? Object.entries(results.byLocation) : [];
  const labels = tableRows.map(([loc]) => loc);
  const firstLocation = tableRows[0]?.[0];
  const buildingsUpfrontData = tableRows.map(([, r]) => r.buildings.costEffectivenessMetrics.upfrontCapitalCost);
  const transportUpfrontData = tableRows.map(([, r]) => r.transport.upfrontCapitalCost);
  const upfrontData = tableRows.map(([, r]) => r.totalUpfrontCost);
  const npvData = tableRows.map(([, r]) => r.totalNPV);

  const popForLoc = (r: (typeof tableRows)[0][1]) => (r.population != null && r.population > 0 ? r.population : 1);
  const upfrontDataNorm = tableRows.map(([, r]) => r.totalUpfrontCost / popForLoc(r));
  const npvDataNorm = tableRows.map(([, r]) => r.totalNPV / popForLoc(r));
  const buildingsUpfrontDataNorm = tableRows.map(([, r]) => r.buildings.costEffectivenessMetrics.upfrontCapitalCost / popForLoc(r));
  const transportUpfrontDataNorm = tableRows.map(([, r]) => r.transport.upfrontCapitalCost / popForLoc(r));

  // Reduction per capita in tons (totalEmissionsReductionMT is already in metric tons, not megatons)
  const reductionPerCapitaTons = (r: (typeof tableRows)[0][1]) =>
    r.population != null && r.population > 0 ? r.totalEmissionsReductionMT / r.population : 0;
  const scatterData = tableRows.map(([loc, r]) => ({
    x: r.costPerTon ?? 0,
    y: normalizePerCapita ? reductionPerCapitaTons(r) : r.totalEmissionsReductionMT,
    loc,
    costPerTon: r.costPerTon,
    reduction: r.totalEmissionsReductionMT,
    reductionPerCapitaTons: reductionPerCapitaTons(r),
  }));

  /** Format y-axis ticks without scientific notation */
  const yAxisTickCallback = (value: string | number) =>
    typeof value === 'number'
      ? value.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 })
      : value;

  const barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => currencyShort(Number(ctx.parsed.y)) } } },
    scales: {
      x: { title: { display: true, text: 'Location' } },
      y: { beginAtZero: true, ticks: { callback: yAxisTickCallback } },
    },
  };

  if (!enabled) return null;

  return (
    <section id="scenario-cost-analysis-section" className="scenario-cost-comparison-section" style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #e5e7eb' }}>
      <details style={{ border: '1px solid #e2e8f0', borderRadius: 8, background: '#fafafa' }}>
        <summary style={{ cursor: 'pointer', padding: '12px 16px', fontWeight: 600, fontSize: '1rem', listStyle: 'none' }}>
          <span style={{ marginRight: 8 }}>▶</span> Scenario Cost Comparison Across Locations
        </summary>
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: 12, marginBottom: 16 }}>
            Compare financial impacts and emissions reduction of the same policy scenario across locations.
          </p>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 48, marginBottom: 24 }}>
          <div className="loading-spinner" style={{ marginBottom: 0 }} aria-hidden />
          <span style={{ fontSize: '0.9375rem', color: '#6b7280' }}>Loading comparison…</span>
        </div>
      )}
      {results && tableRows.length > 0 && !loading && (
        <div style={{ marginBottom: 20, padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <h4 style={{ fontSize: '0.9375rem', marginBottom: 10, fontWeight: 600 }}>How costs and savings are calculated</h4>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.8125rem', color: '#475569', lineHeight: 1.6 }}>
            <li><strong>Period:</strong> All costs and savings are <strong>cumulative over 2023–2045</strong>. Upfront, NPV, cumulative net cost, and fuel savings sum or discount over this window.</li>
            <li><strong>Baseline (BAU):</strong> The baseline already includes state policy (e.g. state EV adoption ramp, grid decarbonization, building codes). Numbers in this table are <strong>incremental to that BAU</strong>—i.e. the extra cost or savings from the local scenario you selected, not the full cost/savings from state policy.</li>
            <li><strong>Buildings:</strong> Upfront = capital (weatherization, reach codes, heat pumps, etc.). Fuel savings = cumulative reduction in utility bills (gas + electricity) from efficiency and electrification vs BAU. Electricity cost stays in buildings; transport EV charging is modeled separately.</li>
            <li><strong>Transport:</strong> Upfront = EV incremental cost + charger cost (no cost for VMT reduction). Fuel savings = net of gasoline savings and EV electricity cost: gasoline use drops (from EV adoption and VMT reduction), while electricity for EVs increases. <strong>VMT reduction is pure savings</strong> (less gasoline, no extra cost in this model). Gasoline savings and EV electricity cost are shown in the sector breakdown.</li>
            <li><strong>Emissions reduction:</strong> Cumulative MT CO₂e (baseline − policy) over 2023–2045, all sectors.</li>
          </ul>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: 8 }}>Locations (select 2–5)</label>
        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>
          {availableLocations.length > 0 ? 'Cities in county, county, and California.' : 'Loading…'}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {availableLocations.map((loc) => (
            <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedLocations.includes(loc)}
                onChange={() => toggleLocation(loc)}
                disabled={!selectedLocations.includes(loc) && selectedLocations.length >= MAX_LOCATIONS}
              />
              <span style={{ fontSize: '0.875rem' }}>{loc}</span>
            </label>
          ))}
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Add location:</label>
          <select
            value=""
            onChange={(e) => {
              const name = e.target.value;
              if (!name) return;
              e.target.value = '';
              if (!selectedLocations.includes(name) && selectedLocations.length < MAX_LOCATIONS) {
                setSelectedLocations((prev) => [...prev, name]);
              }
            }}
            style={{ padding: '6px 10px', fontSize: '0.875rem', minWidth: 200 }}
          >
            <option value="">— All cities and counties —</option>
            <optgroup label="Counties">
              {allCitiesAndCounties.counties.map((name) => (
                <option key={name} value={name} disabled={selectedLocations.includes(name)}>
                  {name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Cities">
              {allCitiesAndCounties.cities.map((name) => (
                <option key={name} value={name} disabled={selectedLocations.includes(name)}>
                  {name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <button
          type="button"
          onClick={runComparison}
          disabled={loading || selectedLocations.length < MIN_LOCATIONS}
          style={{ marginTop: 12, padding: '8px 16px', cursor: loading ? 'wait' : 'pointer' }}
        >
          {loading ? 'Loading…' : 'Update comparison'}
        </button>
        {error && <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: 8 }}>{error}</p>}
      </div>

      <details style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.9375rem' }}>Cost Assumptions</summary>
        <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 8, marginBottom: 12 }}>
          Adjust prices and technology costs. Results update automatically after you change a value. Blank location overrides use the global default.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Energy prices</div>
            <label style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 2 }}>Electricity ($/kWh) — average</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={assumptions.electricityPricePerKwh}
              onChange={(e) => setAssumptions((a) => ({ ...a, electricityPricePerKwh: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            {electricityPriceSource && (
              <p style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 4 }}>
                Source: {electricityPriceSource.utilityName ?? 'California average'}
                {electricityPriceSource.year != null && ` (${electricityPriceSource.year})`}.
                {electricityPriceSource.residential != null && electricityPriceSource.commercial != null && (
                  <> Res: ${electricityPriceSource.residential.toFixed(3)} | Com: ${electricityPriceSource.commercial.toFixed(3)}</>
                )}
              </p>
            )}
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Natural gas ($/therm)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={assumptions.naturalGasPricePerTherm}
              onChange={(e) => setAssumptions((a) => ({ ...a, naturalGasPricePerTherm: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Gasoline ($/gallon)</label>
            <input
              type="number"
              min={0}
              step={0.1}
              value={assumptions.gasolinePricePerGallon}
              onChange={(e) => setAssumptions((a) => ({ ...a, gasolinePricePerGallon: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Technology costs</div>
            <label style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 2 }}>Res. heat pump incremental ($/unit)</label>
            <input
              type="number"
              min={0}
              step={500}
              value={assumptions.resHeatPumpIncrementalCostPerUnit}
              onChange={(e) => setAssumptions((a) => ({ ...a, resHeatPumpIncrementalCostPerUnit: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Com. heat pump incremental ($/equiv. unit)</label>
            <input
              type="number"
              min={0}
              step={1000}
              value={assumptions.comHeatPumpIncrementalCostPerUnit}
              onChange={(e) => setAssumptions((a) => ({ ...a, comHeatPumpIncrementalCostPerUnit: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Avg therms per res. unit</label>
            <input
              type="number"
              min={1}
              step={50}
              value={assumptions.avgThermsPerResUnit}
              onChange={(e) => setAssumptions((a) => ({ ...a, avgThermsPerResUnit: Number(e.target.value) || 1 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Avg therms per com. equiv. unit</label>
            <input
              type="number"
              min={1}
              step={500}
              value={assumptions.avgThermsPerComEquivalentUnit}
              onChange={(e) => setAssumptions((a) => ({ ...a, avgThermsPerComEquivalentUnit: Number(e.target.value) || 1 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>EV incremental ($)</label>
            <input
              type="number"
              min={0}
              step={500}
              value={assumptions.evIncrementalCost}
              onChange={(e) => setAssumptions((a) => ({ ...a, evIncrementalCost: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Charger cost per EV ($)</label>
            <input
              type="number"
              min={0}
              step={100}
              value={assumptions.chargerCostPerEv}
              onChange={(e) => setAssumptions((a) => ({ ...a, chargerCostPerEv: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Weatherization ($/home)</label>
            <input
              type="number"
              min={0}
              step={500}
              value={assumptions.weatherizationCostPerParticipatingHome}
              onChange={(e) => setAssumptions((a) => ({ ...a, weatherizationCostPerParticipatingHome: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Res. reach code ($/unit)</label>
            <input
              type="number"
              min={0}
              step={500}
              value={assumptions.residentialReachCodeCostPerNewUnit}
              onChange={(e) => setAssumptions((a) => ({ ...a, residentialReachCodeCostPerNewUnit: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Com. reach code ($/unit)</label>
            <input
              type="number"
              min={0}
              step={1000}
              value={assumptions.commercialReachCodeCostPerNewUnit}
              onChange={(e) => setAssumptions((a) => ({ ...a, commercialReachCodeCostPerNewUnit: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Financial</div>
            <label style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 2 }}>Discount rate (%)</label>
            <input
              type="number"
              min={0}
              max={20}
              step={0.5}
              value={assumptions.discountRate * 100}
              onChange={(e) => setAssumptions((a) => ({ ...a, discountRate: (Number(e.target.value) || 0) / 100 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Heat pump rebate ($)</label>
            <input
              type="number"
              min={0}
              step={500}
              value={assumptions.heatPumpRebate}
              onChange={(e) => setAssumptions((a) => ({ ...a, heatPumpRebate: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Weatherization rebate ($)</label>
            <input
              type="number"
              min={0}
              step={500}
              value={assumptions.weatherizationRebate}
              onChange={(e) => setAssumptions((a) => ({ ...a, weatherizationRebate: Number(e.target.value) || 0 }))}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Transport tech</div>
            <label style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 2 }}>ICE (mpg)</label>
            <input
              type="number"
              min={10}
              max={60}
              step={1}
              value={iceMpg}
              onChange={(e) => setIceMpg(Number(e.target.value) || DEFAULT_ICE_MPG)}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
            <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>EV (kWh/mile)</label>
            <input
              type="number"
              min={0.2}
              max={0.5}
              step={0.05}
              value={evKwhPerMile}
              onChange={(e) => setEvKwhPerMile(Number(e.target.value) || DEFAULT_EV_KWH_PER_MILE)}
              style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
            />
          </div>
        </div>

        {selectedLocations.length > 0 && (
          <>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Per-location overrides (optional)</div>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 8 }}>Leave blank to use global default.</p>
            <div style={{ overflowX: 'auto', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: 6 }}>Location</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Electricity ($/kWh)</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Natural gas ($/therm)</th>
                    <th style={{ textAlign: 'left', padding: 6 }}>Gasoline ($/gal)</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLocations.map((loc) => (
                    <tr key={loc} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 6 }}>{loc}</td>
                      <td style={{ padding: 6 }}>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          placeholder={String(assumptions.electricityPricePerKwh)}
                          value={locationOverrides[loc]?.electricityPricePerKwh ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            setLocationOverrides((prev) => ({
                              ...prev,
                              [loc]: { ...prev[loc], electricityPricePerKwh: v },
                            }));
                          }}
                          style={{ width: 90, padding: '2px 6px' }}
                        />
                      </td>
                      <td style={{ padding: 6 }}>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          placeholder={String(assumptions.naturalGasPricePerTherm)}
                          value={locationOverrides[loc]?.naturalGasPricePerTherm ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            setLocationOverrides((prev) => ({
                              ...prev,
                              [loc]: { ...prev[loc], naturalGasPricePerTherm: v },
                            }));
                          }}
                          style={{ width: 90, padding: '2px 6px' }}
                        />
                      </td>
                      <td style={{ padding: 6 }}>
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          placeholder={String(assumptions.gasolinePricePerGallon)}
                          value={locationOverrides[loc]?.gasolinePricePerGallon ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            setLocationOverrides((prev) => ({
                              ...prev,
                              [loc]: { ...prev[loc], gasolinePricePerGallon: v },
                            }));
                          }}
                          style={{ width: 90, padding: '2px 6px' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => {
              setAssumptions(DEFAULT_COST_ASSUMPTIONS);
              setIceMpg(DEFAULT_ICE_MPG);
              setEvKwhPerMile(DEFAULT_EV_KWH_PER_MILE);
              setLocationOverrides({});
            }}
            style={{ padding: '6px 12px', fontSize: '0.8125rem' }}
          >
            Reset to defaults
          </button>
          <button
            type="button"
            onClick={() => {
              const next: Record<string, LocationPriceOverrides> = {};
              selectedLocations.forEach((loc) => {
                next[loc] = {
                  electricityPricePerKwh: assumptions.electricityPricePerKwh,
                  naturalGasPricePerTherm: assumptions.naturalGasPricePerTherm,
                  gasolinePricePerGallon: assumptions.gasolinePricePerGallon,
                };
              });
              setLocationOverrides(next);
            }}
            style={{ padding: '6px 12px', fontSize: '0.8125rem' }}
          >
            Apply global values to all locations
          </button>
        </div>

        <details style={{ marginTop: 16, padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>Key assumptions (BAU) by scenario</summary>
          <div style={{ marginTop: 12 }}>
            {currentLocation && (
              <KeyAssumptionsTable
                location={currentLocation}
                scenario={scenario}
                enabled={!!results && tableRows.length > 0}
                options={options}
                embedded
              />
            )}
          </div>
        </details>
      </details>

      {results && tableRows.length > 0 && !loading && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 0 }}>Summary by location</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem' }}>
              <span>Cost view:</span>
              <select
                value={costView}
                onChange={(e) => setCostView(e.target.value as 'incremental_vs_bau' | 'bau_costs' | 'total_costs')}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}
              >
                <option value="incremental_vs_bau">Costs above BAU (incremental local policy)</option>
                <option value="bau_costs">BAU costs (state policy alone vs flat baseline)</option>
                <option value="total_costs">Total costs (state + local vs flat baseline)</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => {
                const header = ['location', 'totalUpfrontCost', 'totalNPV', 'totalCumulativeNetCost', 'totalSavings', 'totalEmissionsReductionTons', 'costPerTon', 'paybackYear', 'costPerCapita', 'reductionPerCapitaTons'];
                const rows = tableRows.map(([loc, r]) => {
                  const totalSavings = r.buildings.annualSavingsSeries.reduce((s, x) => s + x.value, 0) + r.transport.breakdown.fuelSavings;
                  return [
                    loc,
                    r.totalUpfrontCost,
                    r.totalNPV,
                    r.totalCumulativeNetCost,
                    totalSavings,
                    Math.round(r.totalEmissionsReductionMT),
                    r.costPerTon != null ? r.costPerTon.toFixed(2) : '',
                    r.paybackYear != null ? String(r.paybackYear) : 'Never',
                    r.population != null && r.population > 0 ? (r.totalCumulativeNetCost / r.population).toFixed(2) : '',
                    r.population != null && r.population > 0 ? (r.totalEmissionsReductionMT / r.population).toFixed(2) : '',
                  ];
                });
                const csv = [header.join(','), ...rows.map((row) => row.map(escapeCsvCell).join(','))].join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'scenario-cost-comparison.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{ padding: '6px 12px', fontSize: '0.875rem' }}
            >
              Download comparison table
            </button>
          </div>
          <p style={{ fontSize: '0.75rem', color: FINANCIAL_COLORS.muted, marginBottom: 4 }}>
            {FINANCIAL_COLOR_NOTE}
          </p>
          <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>
            Sign conventions: <strong>Cumulative net cost</strong> = undiscounted (costs − savings) over 2023–2045; negative means net savings. <strong>NPV</strong> = present value of (savings − costs); positive = net benefit. <strong>Cost per ton</strong> negative = net savings per ton reduced. <strong>Payback year</strong> = first year when cumulative net cost ≤ 0; only shown if cumulative net cost is ≤ 0 by 2045, otherwise <strong>Never</strong>.
          </p>
          <div style={{ overflowX: 'auto', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: 8 }}>Location</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Upfront Cost</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="Net present value of (savings − costs). Positive = net benefit.">NPV</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="Undiscounted sum of (costs − savings) over 2023–2045. Negative = net savings.">Cumulative Net Cost</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="Cumulative operating savings (buildings) and fuel savings (transport) over 2023–2045.">Savings</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Emissions Reduction (tons)</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="Cumulative net cost ÷ total emissions reduction. Negative = net savings per ton.">Portfolio cost per ton reduced</th>
                  <th style={{ textAlign: 'right', padding: 8 }} title="First year when cumulative net cost ≤ 0 (costs − savings). Only shown if cumulative net cost is ≤ 0 by 2045; otherwise Never.">Payback Year</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Cost per Capita</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Reduction per Capita (tons)</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map(([loc, r]) => {
                  const b = r.buildings.costEffectivenessMetrics;
                  const t = r.transport;
                  const pop = r.population != null && r.population > 0 ? r.population : null;
                  const buildingsSavings = r.buildings.annualSavingsSeries.reduce((s, x) => s + x.value, 0);
                  const transportFuelSavings = t.breakdown.fuelSavings;
                  const totalSavings = buildingsSavings + transportFuelSavings;
                  return (
                    <React.Fragment key={loc}>
                      {/* Total row */}
                      <tr style={{ borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                        <td style={{ padding: 8, color: FINANCIAL_COLORS.neutral }}>{loc}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{currencyShort(r.totalUpfrontCost)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(r.totalNPV, 'npv') }} title="Positive = net benefit; negative = net cost">{currencyShort(r.totalNPV)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(r.totalCumulativeNetCost, 'cumulative') }} title="Negative = net savings">{currencyShort(r.totalCumulativeNetCost)}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{currencyShort(totalSavings)}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{Math.round(r.totalEmissionsReductionMT).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(r.costPerTon ?? undefined, 'cost_per_ton') }} title="Negative = net savings per ton">{r.costPerTon != null ? currencyShort(r.costPerTon) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...paybackCellStyle(r.paybackYear) }}>{r.paybackYear != null ? String(r.paybackYear) : 'Never'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(pop ? r.totalCumulativeNetCost / pop : undefined, 'cost_per_capita') }}>{pop ? currencyShort(r.totalCumulativeNetCost / pop) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{pop ? (r.totalEmissionsReductionMT / pop).toFixed(2) : '—'}</td>
                      </tr>
                      {/* Buildings row */}
                      <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
                        <td style={{ padding: 8, paddingLeft: 24, fontSize: '0.8125rem', color: FINANCIAL_COLORS.neutral }}>Buildings</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{currencyShort(b.upfrontCapitalCost)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(b.netPresentValue, 'npv') }}>{currencyShort(b.netPresentValue)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(b.cumulativeNetCost, 'cumulative') }}>{currencyShort(b.cumulativeNetCost)}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{currencyShort(buildingsSavings)}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{Math.round(r.buildings.totalBuildingEmissionsReductionMT).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(b.costPerTonCO2eReduced ?? undefined, 'cost_per_ton') }}>{b.costPerTonCO2eReduced != null ? currencyShort(b.costPerTonCO2eReduced) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...paybackCellStyle(b.paybackYear) }}>{b.paybackYear != null ? String(b.paybackYear) : 'Never'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(pop ? b.cumulativeNetCost / pop : undefined, 'cost_per_capita') }}>{pop ? currencyShort(b.cumulativeNetCost / pop) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{pop ? (r.buildings.totalBuildingEmissionsReductionMT / pop).toFixed(2) : '—'}</td>
                      </tr>
                      {/* Transport row */}
                      <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
                        <td style={{ padding: 8, paddingLeft: 24, fontSize: '0.8125rem', color: FINANCIAL_COLORS.neutral }}>Transport</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{currencyShort(t.upfrontCapitalCost)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(t.netPresentValue, 'npv') }}>{currencyShort(t.netPresentValue)}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(t.cumulativeNetCost, 'cumulative') }}>{currencyShort(t.cumulativeNetCost)}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{currencyShort(transportFuelSavings)}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{Math.round(t.emissionsReductionMT).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(t.costPerTon ?? undefined, 'cost_per_ton') }}>{t.costPerTon != null ? currencyShort(t.costPerTon) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...paybackCellStyle(t.paybackYear) }}>{t.paybackYear != null ? String(t.paybackYear) : 'Never'}</td>
                        <td style={{ textAlign: 'right', padding: 8, ...financialCellStyle(pop ? t.cumulativeNetCost / pop : undefined, 'cost_per_capita') }}>{pop ? currencyShort(t.cumulativeNetCost / pop) : '—'}</td>
                        <td style={{ textAlign: 'right', padding: 8, color: FINANCIAL_COLORS.neutral }}>{pop ? (t.emissionsReductionMT / pop).toFixed(2) : '—'}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={normalizePerCapita}
                onChange={(e) => setNormalizePerCapita(e.target.checked)}
              />
              Normalize per capita
            </label>
            <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>When on, charts show per-capita values.</span>
          </div>

          <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>
            {normalizePerCapita ? 'Cost breakdown by sector (per capita)' : 'Cost breakdown by sector'}
          </h3>
          <div style={{ height: 220, marginBottom: 24 }}>
            <Bar
              data={{
                labels,
                datasets: [
                  { label: 'Buildings', data: normalizePerCapita ? buildingsUpfrontDataNorm : buildingsUpfrontData, backgroundColor: 'rgba(59, 130, 246, 0.8)', stack: 'stack0' },
                  { label: 'Transport', data: normalizePerCapita ? transportUpfrontDataNorm : transportUpfrontData, backgroundColor: 'rgba(34, 197, 94, 0.8)', stack: 'stack0' },
                ],
              }}
              options={{
                ...barChartOptions,
                scales: {
                  ...barChartOptions.scales,
                  x: { ...barChartOptions.scales?.x, stacked: true },
                  y: { ...barChartOptions.scales?.y, stacked: true, title: { display: true, text: normalizePerCapita ? '$/capita' : undefined } },
                },
                plugins: {
                  ...barChartOptions.plugins,
                  legend: { display: true },
                  tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${currencyShort(Number(ctx.parsed.y))}${normalizePerCapita ? ' per capita' : ''}` } },
                },
              }}
            />
          </div>

          <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>{normalizePerCapita ? 'Upfront cost per capita by location' : 'Upfront cost by location'}</h3>
          <div style={{ height: 220, marginBottom: 24 }}>
            <Bar
              data={{ labels, datasets: [{ label: normalizePerCapita ? 'Upfront $/capita' : 'Upfront cost', data: normalizePerCapita ? upfrontDataNorm : upfrontData, backgroundColor: 'rgba(59, 130, 246, 0.7)' }] }}
              options={barChartOptions}
            />
          </div>

          <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>Cost vs Emissions Reduction</h3>
          <div style={{ height: 280, marginBottom: 24 }}>
            <Scatter
              data={{
                datasets: [{
                  label: 'Locations',
                  data: scatterData,
                  backgroundColor: 'rgba(59, 130, 246, 0.7)',
                  borderColor: 'rgba(59, 130, 246, 1)',
                  pointRadius: 8,
                }],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const raw = ctx.raw as { loc?: string; costPerTon?: number | null; reduction?: number; reductionPerCapitaTons?: number };
                        const loc = raw?.loc ?? ctx.label;
                        const cp = raw?.costPerTon != null ? currencyShort(raw.costPerTon) : '—';
                        const red = normalizePerCapita && raw?.reductionPerCapitaTons != null ? `${raw.reductionPerCapitaTons.toFixed(2)} tons/capita` : raw?.reduction != null ? `${Math.round(raw.reduction).toLocaleString(undefined, { maximumFractionDigits: 0 })} tons` : '—';
                        return [loc, `Cost per ton: ${cp}`, normalizePerCapita ? `Reduction per capita: ${red}` : `Total reduction: ${red}`];
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    title: { display: true, text: 'Portfolio cost per ton ($/tCO₂e)' },
                    beginAtZero: true,
                  },
                  y: {
                    title: { display: true, text: normalizePerCapita ? 'Emissions reduction per capita (tons)' : 'Total emissions reduction (tons)' },
                    beginAtZero: true,
                  },
                },
              }}
            />
          </div>

          <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>{normalizePerCapita ? 'NPV per capita by location' : 'NPV by location'}</h3>
          <div style={{ height: 220, marginBottom: 24 }}>
            <Bar
              data={{ labels, datasets: [{ label: normalizePerCapita ? 'NPV $/capita' : 'NPV', data: normalizePerCapita ? npvDataNorm : npvData, backgroundColor: 'rgba(124, 58, 237, 0.7)' }] }}
              options={barChartOptions}
            />
          </div>

          <details style={{ marginBottom: 24, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.9375rem' }}>Sensitivity analysis</summary>
            <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 8, marginBottom: 12 }}>
              Test cost per ton with gasoline and electricity prices ±20%. Runs 5 scenarios (base, gas +20%, gas −20%, elec +20%, elec −20%).
            </p>
            <button
              type="button"
              disabled={sensitivityLoading || selectedLocations.length < MIN_LOCATIONS}
              onClick={async () => {
                setSensitivityLoading(true);
                try {
                  const locs = selectedLocations.filter(Boolean);
                  const merged = buildMergedOptions(options, assumptions, locationOverrides, locs, iceMpg, evKwhPerMile, costView);
                  const base = await computeScenarioCostResults({ scenario, locations: locs, options: merged });
                  const gasUp = await computeScenarioCostResults({
                    scenario,
                    locations: locs,
                    options: { ...merged, gasolinePricePerGallon: (merged.gasolinePricePerGallon ?? assumptions.gasolinePricePerGallon) * 1.2 },
                  });
                  const gasDown = await computeScenarioCostResults({
                    scenario,
                    locations: locs,
                    options: { ...merged, gasolinePricePerGallon: (merged.gasolinePricePerGallon ?? assumptions.gasolinePricePerGallon) * 0.8 },
                  });
                  const elecUp = await computeScenarioCostResults({
                    scenario,
                    locations: locs,
                    options: {
                      ...merged,
                      priceOverridesByLocation: Object.fromEntries(
                        locs.map((loc) => [loc, { ...merged.priceOverridesByLocation?.[loc], electricityPricePerKwh: (merged.priceOverridesByLocation?.[loc]?.electricityPricePerKwh ?? assumptions.electricityPricePerKwh) * 1.2 }])
                      ),
                      transportElectricityPricePerKwh: assumptions.electricityPricePerKwh * 1.2,
                    },
                  });
                  const elecDown = await computeScenarioCostResults({
                    scenario,
                    locations: locs,
                    options: {
                      ...merged,
                      priceOverridesByLocation: Object.fromEntries(
                        locs.map((loc) => [loc, { ...merged.priceOverridesByLocation?.[loc], electricityPricePerKwh: (merged.priceOverridesByLocation?.[loc]?.electricityPricePerKwh ?? assumptions.electricityPricePerKwh) * 0.8 }])
                      ),
                      transportElectricityPricePerKwh: assumptions.electricityPricePerKwh * 0.8,
                    },
                  });
                  const byLocation: Record<string, { costPerTonBase: number | null; costPerTonMin: number; costPerTonMax: number }> = {};
                  locs.forEach((loc) => {
                    const vals = [
                      base.byLocation[loc]?.costPerTon,
                      gasUp.byLocation[loc]?.costPerTon,
                      gasDown.byLocation[loc]?.costPerTon,
                      elecUp.byLocation[loc]?.costPerTon,
                      elecDown.byLocation[loc]?.costPerTon,
                    ].filter((x): x is number => x != null && Number.isFinite(x));
                    const baseVal = base.byLocation[loc]?.costPerTon ?? null;
                    byLocation[loc] = {
                      costPerTonBase: baseVal,
                      costPerTonMin: vals.length > 0 ? Math.min(...vals) : 0,
                      costPerTonMax: vals.length > 0 ? Math.max(...vals) : 0,
                    };
                  });
                  setSensitivityResults({ byLocation });
                } catch (e) {
                  setSensitivityResults(null);
                } finally {
                  setSensitivityLoading(false);
                }
              }}
              style={{ padding: '6px 12px', fontSize: '0.875rem', marginBottom: 12 }}
            >
              {sensitivityLoading ? 'Running…' : 'Run sensitivity analysis'}
            </button>
            {sensitivityResults && (() => {
              const locs = Object.keys(sensitivityResults.byLocation);
              const sensLabels = locs;
              const minData = locs.map((loc) => sensitivityResults.byLocation[loc].costPerTonMin);
              const baseData = locs.map((loc) => sensitivityResults.byLocation[loc].costPerTonBase ?? 0);
              const maxData = locs.map((loc) => sensitivityResults.byLocation[loc].costPerTonMax);
              const colorFor = (v: number) => {
        const base = getFinancialBarColor(v);
        if (base === FINANCIAL_COLORS.favorable) return 'rgba(15, 159, 110, 0.85)';
        if (base === FINANCIAL_COLORS.unfavorable) return 'rgba(199, 91, 57, 0.85)';
        return 'rgba(107, 114, 128, 0.7)';
      };
              return (
                <div style={{ marginTop: 12 }}>
                  <h4 style={{ fontSize: '0.9375rem', marginBottom: 8 }}>Cost per ton: Min / Base / Max</h4>
                  <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 8 }}>Green = net savings per ton; red = net cost per ton. Tooltip: negative = net savings.</p>
                  <div style={{ height: 220 }}>
                    <Bar
                      data={{
                        labels: sensLabels,
                        datasets: [
                          { label: 'Min', data: minData, backgroundColor: minData.map(colorFor) },
                          { label: 'Base', data: baseData, backgroundColor: baseData.map(colorFor) },
                          { label: 'Max', data: maxData, backgroundColor: maxData.map(colorFor) },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom' },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => `${ctx.dataset.label}: ${currencyShort(Number(ctx.parsed.y))} (negative = net savings)`,
                            },
                          },
                        },
                        scales: {
                          x: { title: { display: true, text: 'Location' } },
                          y: {
                            title: { display: true, text: 'Cost per ton ($/tCO₂e)' },
                            ticks: { callback: (v) => (typeof v === 'number' ? currencyShort(v) : v) },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })()}
          </details>
        </>
      )}

      {results && tableRows.length === 0 && !loading && (
        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>No cost data for selected locations. Ensure at least 2 locations are selected and run comparison.</p>
      )}
        </div>
      </details>
    </section>
  );
};
